import React, { useState, useEffect } from "react";
import { postQuote } from "services/booking.services";
import { validateRequestQuoteFields } from "helpers/validation";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import styles from "./styles.module.scss";
import {
  CustomInput,
  FormButton,
  FormDropdown,
  Checkbox,
  CustomTextarea,
  CustomDatePicker,
  MessageBox,
} from "reusableComponents";
import { useModal } from "context";
import { useNavigate } from "react-router";
import getFilteredDispositionTypes from "helpers/getSpaceDispositionTypes";
import { fetchSpaceData } from "services/spacePage.services";
import { useLanguage } from "context";

const RequestQuotationForm = ({ spaceId, spaceType = "seminar", spaceData: propSpaceData }) => {
  const currentUser = useSelector((state) => state.auth.currentUser);
  const bookingFilters = useSelector((state) => state.filters);
  const navigate = useNavigate();
  const { t } = useTranslation("global");
  const { closeModal, openModal } = useModal();
  const { language } = useLanguage();
  
  // Local state for space data if not provided via props
  const [localSpaceData, setLocalSpaceData] = useState(null);
  const [loadingSpace, setLoadingSpace] = useState(false);
  
  // Use prop spaceData if available, otherwise use local
  const spaceData = propSpaceData || localSpaceData;

  // Fetch space data if not provided as prop
  useEffect(() => {
    if (!propSpaceData && spaceId) {
      setLoadingSpace(true);
      fetchSpaceData(spaceId, language)
        .then(data => {
          setLocalSpaceData(data);
        })
        .catch(error => {
          console.error("[RequestQuotationForm] Failed to fetch space data:", error);
        })
        .finally(() => {
          setLoadingSpace(false);
        });
    }
  }, [propSpaceData, spaceId, language]);

  // Helper function to format date from filters
  const getPrefilledDate = () => {
    if (bookingFilters?.from && bookingFilters.from !== "") {
      try {
        // Handle different date formats
        let dateObj;
        if (typeof bookingFilters.from === 'string' && bookingFilters.from.includes('-')) {
          // YYYY-MM-DD format
          const [year, month, day] = bookingFilters.from.split('-').map(Number);
          dateObj = new Date(year, month - 1, day);
        } else {
          dateObj = new Date(bookingFilters.from);
        }
        
        // Check if valid date
        if (!isNaN(dateObj.getTime())) {
          return dateObj;
        }
      } catch (e) {
        console.error("Error parsing date from filters:", e);
      }
    }
    return null;
  };

  // Get prefilled capacity from filters
  const getPrefilledCapacity = () => {
    if (bookingFilters?.capacity && bookingFilters.capacity > 0) {
      return bookingFilters.capacity;
    }
    return 1;
  };

  // Get disposition types based on space data
  const getDispositionOptions = () => {
    // Safeguard: if spaceData is undefined or null, return empty array
    if (!spaceData) {
      return [];
    }
    
    try {
      // Use the helper to filter based on space data
      const filteredTypes = getFilteredDispositionTypes(spaceData, t, formData.participants);
      
      // If no filtered types, return empty array
      if (!filteredTypes || filteredTypes.length === 0) {
        return [];
      }
      
      // Convert to dropdown options format with icons
      return filteredTypes.map(type => ({
        value: type.key,
        label: `${type.name} (${type.count} ${t('provide.step2.area.people') || 'people'})`,
        icon: type.Icon,
        count: type.count,
        name: type.name // Store the clean name for submission
      }));
    } catch (error) {
      console.error("Error getting disposition types:", error);
      return [];
    }
  };

  const [formData, setFormData] = useState({
    name: currentUser?.last_name || "",
    firstname: currentUser?.first_name || "",
    phone: currentUser?.phone || "",
    company: currentUser?.social_reason || "",
    email: currentUser?.email || "",
    seminar_event: (spaceType === "seminar" || spaceType === "teambuilding" || spaceType === "conference_room") ? "half_day" : "hour",
    participants: getPrefilledCapacity(),
    start_event: getPrefilledDate(),
    end_event: getPrefilledDate(),
    place_id: spaceId,
    message: "",
    type: "other",
    date_flexible: true,
    locale: "fr",
    audio_visual_equipment: "",
    setup: null, // Stores the disposition type key
    disposition_name: null, // Stores the disposition type name for submission
    catering: "",
  });

  const [errors, setErrors] = useState([]);
  const [touchedFields, setTouchedFields] = useState({});
  const [responseMessage, setResponseMessage] = useState("");
  const [loading, setLoading] = useState(false);

  // Update form when Redux filters change
  useEffect(() => {
    const prefilledDate = getPrefilledDate();
    const prefilledCapacity = getPrefilledCapacity();
    
    setFormData(prev => ({
      ...prev,
      participants: prefilledCapacity,
      start_event: prefilledDate || prev.start_event
    }));
  }, [bookingFilters?.from, bookingFilters?.capacity]);

  // Get duration options based on space type
  const getDurationOptions = () => {
    if (spaceType === "seminar" || spaceType === "teambuilding" || spaceType === "conference_room") {
      return [
        { value: "half_day", label: t("search.filters.halfDay") },
        { value: "day", label: t("search.filters.day") },
        { value: "residential", label: t("search.filters.residential") },
      ];
    } else {
      return [
        { value: "hour", label: t("search.filters.hour") },
        { value: "half_day", label: t("search.filters.halfDay") },
        { value: "day", label: t("search.filters.day") },
        { value: "month", label: t("search.filters.month") },
      ];
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });

    if (touchedFields[name]) {
      validateInput(name, value);
    }
  };

  const handleDateChange = (name, date) => {
    setFormData({
      ...formData,
      [name]: date,
    });

    if (touchedFields[name]) {
      validateInput(name, date);
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouchedFields({ ...touchedFields, [name]: true });
    validateInput(name, value);
  };

  const validateInput = (name, value) => {
    const errorMessage = validateRequestQuoteFields(name, value, t, formData);
    setErrors((prevErrors) => {
      const filteredErrors = Array.isArray(prevErrors)
        ? prevErrors.filter((err) => err.code !== name)
        : [];
      return errorMessage
        ? [...filteredErrors, { code: name, message: errorMessage }]
        : filteredErrors;
    });
  };

  const validateForm = () => {
    const newErrors = [];
    Object.keys(formData).forEach((key) => {
      const error = validateRequestQuoteFields(key, formData[key], t, formData);
      if (error) {
        newErrors.push({ code: key, message: error });
      }
    });
    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setResponseMessage("");
    setErrors([]);

    try {
      // Use disposition_name if available, otherwise fallback to setup key
      const dispositionValue = formData.disposition_name || formData.setup;

      const result = await postQuote({
        ...formData,
        start_event: formData.start_event
          ? new Date(formData.start_event).toISOString().split("T")[0]
          : null,
        end_event: formData.end_event
          ? new Date(formData.end_event).toISOString().split("T")[0]
          : null,
        disposition_type: dispositionValue, // Send the name instead of the key
      });

        // ADD THIS HERE - right after successful API response
  window.dataLayer.push({
    event: 'question_form_success',
    form_name: 'poser_une_question',
    space_id: spaceId // if available at this point
  });
  
      setResponseMessage(result.message || "Form submitted successfully.");
      closeModal();
      setTimeout(() => {
        openModal({
          title: t("modal.requestbooking.successHeader") || "Success",
          content: <p>{t("modal.requestbooking.successText") || "Form submitted successfully."}</p>,
          type: "success",
        });
      }, 300);
    } catch (error) {
      console.log("Caught error:", error);
      if (Array.isArray(error.data)) {
        console.log(error);
        setErrors(error.data);
      } else {
        setResponseMessage(error.message || "An unexpected error occurred.");
      }
    } finally {
      setLoading(false);
    }
  };

  const getErrorMessage = (field) => {
    if (Array.isArray(errors)) {
      const errorObj = errors.find((err) => err.code === field);
      return errorObj?.message || "";
    }
    return "";
  };

  // Compute disposition options
  const dispositionOptions = spaceData ? getDispositionOptions() : [];
  const isDisabled = !spaceData || loadingSpace || dispositionOptions.length === 0;

  return (
    <div className={styles.quoteForm}>
      <form onSubmit={handleSubmit}>
        {loadingSpace && (
          <MessageBox type="info" message="Loading space data..." />
        )}
        <div className={styles.gridContainer}>
          {/* Row 1: Name, Firstname, Phone */}
          {[
            {
              name: "name",
              placeholder: t("modal.requestbooking.name"),
              required: true,
            },
            {
              name: "firstname",
              placeholder: t("modal.requestbooking.firstname"),
              required: true,
            },
            {
              name: "phone",
              placeholder: t("modal.requestbooking.phone"),
              type: "tel",
              required: false,
            },
          ].map(({ name, placeholder, type = "text", required = false }) => (
            <CustomInput
              labelAbove
              key={name}
              name={name}
              type={type}
              label={placeholder}
              value={formData[name]}
              error={getErrorMessage(name)}
              handleChange={handleChange}
              handleBlur={handleBlur}
              required={required}
            />
          ))}

          {/* Row 2: Email, Company, Audio Visual Equipment */}
          {[
            {
              name: "email",
              placeholder: t("modal.requestbooking.email"),
              type: "email",
              required: true,
            },
            { 
              name: "company", 
              placeholder: t("modal.requestbooking.company") 
            },
            {
              name: "audio_visual_equipment",
              placeholder: t("modal.requestbooking.audio_visual_equipment"),
            },
          ].map(({ name, placeholder, type = "text", required = false }) => (
            <CustomInput
              labelAbove
              key={name}
              name={name}
              type={type}
              label={placeholder}
              value={formData[name]}
              error={getErrorMessage(name)}
              handleChange={handleChange}
              handleBlur={handleBlur}
              required={required}
            />
          ))}

          {/* Row 3: Catering, Disposition Type, Number of Participants */}
          <CustomInput
            labelAbove
            name="catering"
            type="text"
            label={t("modal.requestbooking.catering")}
            value={formData.catering}
            error={getErrorMessage("catering")}
            handleChange={handleChange}
            handleBlur={handleBlur}
            required={false}
          />
          <div className={styles.dispositionWrapper}>
            <FormDropdown
              required
              labelAbove
              showIcons={true}
              label={t("modal.requestbooking.disposition") || "Disposition Type"}
              options={dispositionOptions}
              selected={formData.setup}
              onSelect={(value) => {
                // Find the selected option to get its name
                const selectedOption = dispositionOptions.find(opt => opt.value === value);
                setFormData({ 
                  ...formData, 
                  setup: value,
                  disposition_name: selectedOption?.name || value
                });
                if (touchedFields.setup) {
                  validateInput("setup", value);
                }
              }}
              placeholder={t("modal.requestbooking.selectDisposition") || "Select disposition type"}
              error={getErrorMessage("setup")}
              disabled={isDisabled}
            />
            {!spaceData && !loadingSpace && (
              <MessageBox type="warning" message="Unable to load space data. Please try again." />
            )}
            {spaceData && dispositionOptions.length === 0 && (
              <MessageBox type="info" message="No disposition types available for this space." />
            )}
          </div>
          <CustomInput
            labelAbove
            id="participants"
            name="participants"
            type="number"
            min="1"
            label={t("modal.requestbooking.participants")}
            value={formData.participants}
            handleChange={handleChange}
            handleBlur={handleBlur}
            error={getErrorMessage("participants")}
            required
          />

          {/* Row 4: Start Date, End Date, Duration */}
          <CustomDatePicker
            styled
            label={t("modal.requestbooking.start_eventLabel")}
            selectedDate={formData.start_event}
            onDateChange={(date) => handleDateChange("start_event", date)}
            error={getErrorMessage("start_event")}
            required
          />
          <CustomDatePicker
            styled
            label={t("modal.requestbooking.end_eventLabel")}
            selectedDate={formData.end_event}
            onDateChange={(date) => handleDateChange("end_event", date)}
            error={getErrorMessage("end_event")}
            required
          />
          <FormDropdown
            required
            labelAbove
            label={t("modal.requestbooking.event")}
            options={getDurationOptions()}
            selected={formData.seminar_event}
            onSelect={(value) =>
              setFormData({ ...formData, seminar_event: value })
            }
            placeholder={t("")}
            error={getErrorMessage("seminar_event")}
          />

          {/* Row 5: Date Flexible checkbox (full width) */}
          <div className={styles.fullWidth}>
            <Checkbox
              isSquare
              iconType
              checked={formData.date_flexible}
              onChange={handleChange}
              label={t("modal.requestbooking.date_flexible")}
              name="date_flexible"
            />
          </div>
        </div>

        <CustomTextarea
          required
          label={t("modal.requestbooking.message")}
          placeholder={t("modal.requestbooking.message")}
          value={formData.message}
          onChange={handleChange}
          name="message"
          error={getErrorMessage("message")}
        />
        {responseMessage && (
          <MessageBox
            type="success"
            message={responseMessage}
            className={styles.response}
          />
        )}
        <FormButton
          loading={loading}
          variant="gray"
          label={t("buttons.send")}
          type="submit"
          disabled={loading}
          className={styles.submitBtn}
        />
      </form>
    </div>
  );
};

export default RequestQuotationForm;